import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { studentApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  PageHeader,
  useToast,
} from '@/components/ui';
import { ProposalList } from '@/features/resume/ProposalList';
import {
  renderCertification,
  renderEducation,
  renderExperience,
  renderProject,
} from '@/features/resume/render';
import {
  EMPTY_SELECTION,
  count,
  selectAll,
  selectable,
  selectableSkills,
  toggle,
} from '@/features/resume/selection';
import { SkillProposals } from '@/features/resume/SkillProposals';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * What we read out of one CV, for the student to accept or ignore.
 *
 * Nothing on this screen has happened yet. Every tick box starts empty and the
 * only call that writes anything is the one behind the button at the bottom --
 * which sends exactly what was ticked and nothing else.
 */
export function StudentCvReviewPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [outcome, setOutcome] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['resume-parse', id],
    queryFn: () => studentApi.getResumeParse(id),
    // A CV that has never been read is a 404 with an answer, not a failure to
    // retry: the answer is "press the button".
    retry: false,
  });

  const read = useMutation({
    mutationFn: () => studentApi.parseResume(id),
    onSuccess: (parse) => {
      queryClient.setQueryData(['resume-parse', id], parse);
      void queryClient.invalidateQueries({ queryKey: ['student-resumes'] });
      setSelection(EMPTY_SELECTION);
      setOutcome(null);
    },
    onError: (readError) => toast.error(errorMessage(readError, 'Could not read the file')),
  });

  const apply = useMutation({
    mutationFn: () => studentApi.applyResumeParse(id, selection),
    onSuccess: (result) => {
      setOutcome(result);
      setSelection(EMPTY_SELECTION);
      toast.success('Profile updated');
      // The suggestions themselves do not change, but which of them the
      // profile already holds does.
      void refetch();
      ['student-profile', 'student-dashboard', 'student-skills', 'student-portfolio'].forEach(
        (key) => queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (applyError) => toast.error(errorMessage(applyError)),
  });

  const header = (
    <PageHeader
      title="What we read in your CV"
      description="Nothing here is on your profile yet. Tick what you want to keep."
      action={
        <LinkButton to={paths.student.cv} variant="secondary" size="sm">
          Back to CVs
        </LinkButton>
      }
    />
  );

  if (isPending) return <LoadingState label="Loading what we found…" />;

  if (isError) {
    if (error?.status !== 404) {
      return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
    }
    return (
      <div>
        {header}
        <Card>
          <EmptyState
            icon={<Icon name="document" size={22} />}
            title="This CV has not been read yet"
            description="We will pull out your skills, education, experience and projects and show you what we found. Nothing is added to your profile until you say so."
            action={
              <Button onClick={() => read.mutate()} isLoading={read.isPending}>
                <Icon name="star" size={16} />
                Read this CV
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (data.status === 'FAILED') {
    return (
      <div>
        {header}
        <Alert tone="warning" title="We could not read this file">
          {data.failure_reason}
        </Alert>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={() => read.mutate()} isLoading={read.isPending} variant="secondary">
            <Icon name="reset" size={16} />
            Try again
          </Button>
          <LinkButton to={paths.student.cv} variant="ghost">
            <Icon name="upload" size={16} />
            Upload a different file
          </LinkButton>
        </div>
      </div>
    );
  }

  const picked = count(selection);
  // A reading stored before projects were read has none.
  const projects = data.projects ?? [];
  const linksAvailable =
    data.contact.linkedin_available ||
    data.contact.github_available ||
    data.contact.portfolio_available;

  return (
    <div className="pb-24">
      {header}

      {data.warnings.map((warning) => (
        <Alert key={warning} tone="info" className="mb-6" title="About this reading">
          {warning}
        </Alert>
      ))}

      {outcome && <ApplySummary outcome={outcome} />}

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Skills"
              description={found(data.skills.length)}
              action={
                <div className="flex items-center gap-2">
                  <SelectedBadge
                    picked={selection.skills.length}
                    total={selectableSkills(data.skills).length}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setSelection(selectAll(data))}>
                    <Icon name="check" size={15} />
                    Select everything
                  </Button>
                </div>
              }
            />
            <SkillProposals
              skills={data.skills}
              selected={selection.skills}
              onToggle={(name) => setSelection((current) => toggle(current, 'skills', name))}
            />
          </Card>

          {/* Each group gets its own card so the header can carry the counts;
              ProposalList's own heading stays for screen readers only. */}
          <ProposalCard
            title="Education"
            items={data.education}
            picked={selection.education.length}
          >
            <ProposalList
              title={<span className="sr-only">Education</span>}
              items={data.education}
              selected={selection.education}
              onToggle={(index) => setSelection((current) => toggle(current, 'education', index))}
              render={renderEducation}
              empty="Nothing we could read under an education heading."
            />
          </ProposalCard>

          <ProposalCard
            title="Experience"
            items={data.experience}
            picked={selection.experience.length}
          >
            <ProposalList
              title={<span className="sr-only">Experience</span>}
              items={data.experience}
              selected={selection.experience}
              onToggle={(index) => setSelection((current) => toggle(current, 'experience', index))}
              render={renderExperience}
              empty="Nothing we could read under an experience heading."
            />
          </ProposalCard>

          <ProposalCard
            title="Certifications"
            items={data.certifications}
            picked={selection.certifications.length}
          >
            <ProposalList
              title={<span className="sr-only">Certifications</span>}
              items={data.certifications}
              selected={selection.certifications}
              onToggle={(index) =>
                setSelection((current) => toggle(current, 'certifications', index))
              }
              render={renderCertification}
              empty="No certificates found."
            />
          </ProposalCard>

          {/* Projects go to the portfolio, never to experience. */}
          <ProposalCard title="Projects" items={projects} picked={selection.projects.length}>
            <ProposalList
              title={<span className="sr-only">Projects</span>}
              items={projects}
              selected={selection.projects}
              onToggle={(index) => setSelection((current) => toggle(current, 'projects', index))}
              render={renderProject}
              empty="Nothing we could read under a projects heading."
            />
          </ProposalCard>

          {(data.headline_available || linksAvailable) && (
            <Card>
              <CardHeader
                title="Profile details"
                action={
                  <SelectedBadge
                    picked={(selection.headline ? 1 : 0) + (selection.links ? 1 : 0)}
                    total={(data.headline_available ? 1 : 0) + (linksAvailable ? 1 : 0)}
                  />
                }
              />
              <div className="space-y-3">
                {data.headline_available && (
                  <div className={checkRow(selection.headline)}>
                    <Checkbox
                      checked={selection.headline}
                      onChange={() =>
                        setSelection((current) => ({ ...current, headline: !current.headline }))
                      }
                      label={
                        <>
                          <span className="text-ink">Use this as your headline:</span>{' '}
                          <span className="italic">“{data.headline}”</span>
                        </>
                      }
                    />
                  </div>
                )}
                {linksAvailable && (
                  <div className={checkRow(selection.links)}>
                    <Checkbox
                      checked={selection.links}
                      onChange={() =>
                        setSelection((current) => ({ ...current, links: !current.links }))
                      }
                      label={
                        <>
                          <span className="text-ink">Add the links we found:</span>{' '}
                          {[
                            data.contact.linkedin_available && 'LinkedIn',
                            data.contact.github_available && 'GitHub',
                            data.contact.portfolio_available && 'a portfolio',
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </>
                      }
                    />
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <ContactCard contact={data.contact} />
          <ReadingCard parse={data} onReread={() => read.mutate()} rereading={read.isPending} />
        </div>
      </div>

      <div className="bg-surface border-line rounded-card sticky bottom-4 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 border px-5 py-3 shadow-lg">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className={
              picked === 0
                ? 'bg-surface-muted text-ink-muted grid size-9 place-items-center rounded-full text-sm font-semibold tabular-nums'
                : 'bg-brand-600 grid size-9 place-items-center rounded-full text-sm font-semibold text-white tabular-nums'
            }
          >
            {picked}
          </span>
          <p className="text-ink-muted text-sm">
            {picked === 0
              ? 'Nothing selected yet.'
              : `${picked} ${picked === 1 ? 'thing' : 'things'} selected.`}
          </p>
        </div>
        <Button onClick={() => apply.mutate()} disabled={picked === 0} isLoading={apply.isPending}>
          <Icon name="check" size={16} />
          Add to my profile
        </Button>
      </div>
    </div>
  );
}

/** "3 found", or "None found" -- the count a section header carries. */
function found(total) {
  return total === 0 ? 'None found' : `${total} found`;
}

/** How many of the tickable suggestions in a section are ticked. */
function SelectedBadge({ picked, total }) {
  if (total === 0) return null;
  return (
    <Badge tone={picked > 0 ? 'brand' : 'neutral'} dot={picked > 0}>
      {picked} of {total} selected
    </Badge>
  );
}

/** A tick-box row that reads as selected once it is ticked. */
function checkRow(checked) {
  return checked
    ? 'border-brand-200 bg-brand-50/40 rounded-input border px-3 py-2.5 transition'
    : 'border-line hover:border-brand-200 rounded-input border px-3 py-2.5 transition';
}

/** One section of the checklist: a header with its counts, then the rows. */
function ProposalCard({ title, items, picked, children }) {
  return (
    <Card>
      <CardHeader
        title={title}
        description={found(items.length)}
        action={<SelectedBadge picked={picked} total={selectable(items).length} />}
      />
      {children}
    </Card>
  );
}

/** What actually happened, including everything that did not. */
function ApplySummary({ outcome }) {
  const added = [
    outcome.added_skills && `${outcome.added_skills} skills`,
    outcome.added_education && `${outcome.added_education} education entries`,
    outcome.added_experience && `${outcome.added_experience} roles`,
    outcome.added_certifications && `${outcome.added_certifications} certificates`,
    outcome.added_projects && `${outcome.added_projects} portfolio projects`,
    outcome.headline_set && 'your headline',
    outcome.added_links.length > 0 && outcome.added_links.join(' and '),
  ].filter(Boolean);

  return (
    <Alert
      tone={added.length > 0 ? 'success' : 'info'}
      className="mb-6"
      title={added.length > 0 ? `Added ${added.join(', ')}` : 'Nothing was added'}
    >
      <p>Your profile is {outcome.profile_completion}% complete.</p>
      {outcome.skipped.length > 0 && (
        <ul className="mt-2 list-inside list-disc">
          {outcome.skipped.map((item) => (
            <li key={`${item.item}-${item.reason}`}>
              {item.item} — {item.reason.toLowerCase()}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
}

/**
 * The contact details we read, shown so the student can confirm the file is
 * the one they meant. None of it is applied: the name and address on the
 * account are identity, and a CV is not proof of who owns one.
 */
function ContactCard({ contact }) {
  const rows = [
    ['Name', contact.full_name],
    ['Email', contact.email],
    ['Phone', contact.phone],
  ].filter(([, value]) => value);

  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader title="Who this CV says you are" />
      <dl className="divide-line divide-y text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 py-2 first:pt-0">
            <dt className="text-ink-muted">{label}</dt>
            <dd className="text-ink truncate">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-ink-muted text-caption mt-3">
        We never change your account name, email or phone from a CV. This is here so you can check
        you uploaded the right file.
      </p>
    </Card>
  );
}

function ReadingCard({ parse, onReread, rereading }) {
  return (
    <Card>
      <CardHeader title="About this reading" />
      <p className="text-ink-muted text-sm">
        {parse.structured
          ? `We recognised ${parse.sections_found.length} sections in ${parse.file_name}.`
          : `We found no headings we recognise in ${parse.file_name}.`}
      </p>
      <p className="text-ink-muted text-caption mt-2">
        Read by the {parse.engine} reader. Changed the file? Read it again.
      </p>
      <Button
        variant="secondary"
        size="sm"
        className="mt-3"
        onClick={onReread}
        isLoading={rereading}
      >
        <Icon name="reset" size={15} />
        Read it again
      </Button>
    </Card>
  );
}
